/**
 * THEME-AWARE INK FOR PROJECTION OVERLAYS — the one declaration, read by every consumer.
 *
 * <h2>Why this file exists</h2>
 *
 * The forward-projection studies (recent-pivot Fibonacci, SK360, Time Projection) were styled
 * against a WHITE canvas and the workspace now defaults to DARK. Measured against the dark
 * canvas, on 2026-11-01:
 *
 * <pre>
 *   vLine label line1   #333333   1.50 : 1    invisible — and that is EVERY vLine study
 *   vLine label line2   #666666   3.30 : 1    marginal
 *   fib P1/P2 anchor    #455a64   2.61 : 1    below the 3:1 floor
 * </pre>
 *
 * A graphical object needs 3:1 to register at all, so those are not "faint" — the labels are not
 * there. That is what made the Recent Pivot Fib tool look as though it had not fired: it had, and
 * it drew ten marks nobody could read. The lines themselves are proven present in the live chart
 * option; only their ink was wrong.
 *
 * <h2>⚠ THE BRIGHT ACCENTS THAT WERE ASKED FOR FAIL ON THE OTHER CANVAS</h2>
 *
 * The brief suggested `#64B5F6`, `#81D4FA` or `#FFD54F` as single replacement values. Measured
 * against both canvases:
 *
 * <pre>
 *                      on #0d1117 dark     on #f9fafe light
 *   #64B5F6                 8.55                2.12        FAILS light
 *   #81D4FA                11.47                1.58        FAILS light
 *   #FFD54F                13.41                1.35        FAILS light
 * </pre>
 *
 * They are bright BECAUSE they are light, which is exactly what makes them disappear on a white
 * chart — so adopting one as a single value would move the defect rather than remove it. Light
 * mode is a shipped theme with a toggle in the app bar, and this repository has already paid for
 * that mistake once: see the panel-surfaces note, where replacing pastels with dark tints would
 * have left the same panels unreadable for anyone who clicked the Sun.
 *
 * Of nineteen candidates measured, only five clear 3:1 on BOTH canvases — `#0277bd`, `#7E57C2`,
 * `#E65100`, `#EF5350`, `#78909C` — and none of them is bright on dark, which was the point.
 *
 * So each accent is a PAIR, resolved from the theme store at draw time. The reader on dark gets
 * the bright value the brief asked for (two of the three are its own suggestions); the reader on
 * light keeps a saturated one. {@code overlayInk.test.js} asserts every value clears the floor on
 * its OWN canvas, and separately that the single-value alternative would fail — so reverting to
 * one hex fails loudly rather than quietly costing the other theme.
 *
 * <h2>The label ink is DERIVED, not chosen</h2>
 *
 * {@link overlayLabelInk} is {@code markOn(canvasFor(mode))} — the existing helper that picks
 * whichever of ink and paper contrasts more. So the label fix adds no new colour declaration at
 * all, and it cannot drift from the canvas constants, which {@code themeCanvas.test.js} already
 * pins against the themes themselves.
 *
 * <h2>⚠ WIDTH IS NOT RAISED GLOBALLY, and that is deliberate</h2>
 *
 * The brief asks for the vLine width to go from 1 to 2. The renderer's default is NOT changed,
 * because `thickness` already carries a deliberate hierarchy in two studies — `fixedCycles` draws
 * its anchor at 2 against 1 for the rest, and `spiralInspectOverlays` encodes VERIFIED against
 * unconfirmed as 2 against 1. Raising the floor to 1.5 or 2 would compress both distinctions to
 * nothing, and the symptom would be two studies quietly losing a channel of meaning. The width is
 * therefore set EXPLICITLY on the figures the brief is about, via {@link PROJECTION_WIDTH}.
 */

import { themeMode } from './themeMode';
import { markOn, canvasFor, rgbaFromHex, contrastRatio } from './cycleColors';

/**
 * The line width for a projection overlay.
 *
 * <p>Set per-overlay rather than as a renderer default — see the width note in the file header.
 */
export const PROJECTION_WIDTH = 2;

/**
 * Opacity for a projection line or vertical guide — ⚠ THEME-AWARE, and it has to be.
 *
 * <p>The renderer's default is 0.3, which is right for a background stripe a reader audits price
 * against and wrong for a guide that IS the finding.
 *
 * <p>⚠ TRANSLUCENCY IS SAFE ON DARK AND UNSAFE ON LIGHT, which is not obvious and is why this is
 * a pair rather than the single 0.7 the brief asked for. A dark canvas composites toward black, so
 * a bright line stays bright; a light canvas composites toward white and washes a saturated one
 * out. Solved numerically for the minimum alpha that still clears 3:1 against its own canvas:
 *
 * <pre>
 *                        on #0d1117 dark     on #f9fafe light
 *   anchor                    0.50                0.65
 *   historical                0.50                0.74
 *   future                    0.50                0.84   &lt;-- the binding case
 *   SK-Grid                   0.50                0.77
 * </pre>
 *
 * So on dark anything from 0.5 up is legible and the brief's 0.65–0.8 band is comfortable; on
 * light, 0.7 would put three of the four BELOW the floor — `#E65100` at 0.7 measures 2.71:1. Hence
 * 0.78 on dark (inside the band that was asked for) and 0.92 on light (margin over the 0.84 the
 * worst accent needs). `overlayInk.test.js` re-derives both from the accents, so moving a hex
 * cannot silently invalidate the alpha chosen for it.
 *
 * <p>⚠ For a VERTICAL, read from {@code overlay.verticalOpacity} only — a per-vertical `opacity`
 * key is silently ignored by the renderer, so it must be set on the overlay.
 */
export const PROJECTION_OPACITY = Object.freeze({ dark: 0.78, light: 0.92 });

/** The opacity for the active theme. */
export const projectionOpacity = (mode = themeMode()) => pickForTheme(PROJECTION_OPACITY, mode);

/** The floor every accent must clear against its own canvas, for a graphical object. */
export const CONTRAST_FLOOR = 3;

/**
 * The accent pairs. Keyed by what the mark MEANS, never by its colour.
 *
 * <p>A name like `blue` would have to change when the hue did, so every consumer would change
 * with it; `historical` and `future` are properties of the mark and survive a palette decision.
 *
 * <ul>
 *   <li><b>anchor</b> — the measured-from pivots. Neutral, because they are not a prediction.</li>
 *   <li><b>historical</b> — a target that has already been reached, so it is checkable.</li>
 *   <li><b>future</b> — a target that has not, so it is a claim. Warm, and the brightest of the
 *       three on either canvas, because it is the one a reader came to look at.</li>
 * </ul>
 */
export const PROJECTION_ACCENTS = Object.freeze({
  anchor: Object.freeze({ dark: '#B0BEC5', light: '#455A64' }),
  historical: Object.freeze({ dark: '#4FC3F7', light: '#0277BD' }),
  future: Object.freeze({ dark: '#FFD54F', light: '#E65100' }),
});

/** Every role name, so a test can sweep them rather than listing them a second time. */
export const PROJECTION_ROLES = Object.freeze(Object.keys(PROJECTION_ACCENTS));

/**
 * One half of a {dark, light} pair, for the active theme.
 *
 * <p>Exported so a study can OWN its own hue — as {@code sk360.js} already does — without each of
 * them restating how a theme is resolved. One mechanism, many palettes: the alternative is either
 * a central table that grows a row per study, or N copies of `mode === 'dark' ? … : …` that drift
 * the first time the store's vocabulary changes.
 *
 * <p>⚠ RETURNS NULL for anything that is not a complete pair, rather than falling back to one
 * side. A half-declared pair would draw correctly on one theme and silently wrong on the other,
 * which is the exact defect this module exists to remove.
 *
 * <p>⚠ TYPE-AGNOSTIC about the values, deliberately. The first version required both halves to be
 * STRINGS, which silently returned null for the numeric opacity pair — so the alpha resolved to
 * null, composited to the canvas itself, and every line would have been drawn at zero contrast.
 * Caught by `overlayInk.test.js`, which composites each accent at its own theme's alpha; a test
 * that only checked the colours would have passed. What a pair needs is both halves PRESENT, not
 * both halves of one type.
 */
export function pickForTheme(pair, mode = themeMode()) {
  if (!pair || typeof pair !== 'object') return null;
  if (pair.dark === undefined || pair.dark === null) return null;
  if (pair.light === undefined || pair.light === null) return null;
  return mode === 'dark' ? pair.dark : pair.light;
}

/**
 * The accent for a role on the active theme.
 *
 * <p>⚠ AN UNKNOWN ROLE RETURNS NULL rather than a default colour. A fallback hue would make a
 * mistyped role draw in a colour that means something else — which reads as a study marking the
 * wrong thing, and is the one failure mode a palette helper must not have.
 *
 * @param {string} role one of {@link PROJECTION_ROLES}
 * @param {string} [mode] overridden only by tests; production reads the store
 */
export function accentFor(role, mode = themeMode()) {
  return pickForTheme(PROJECTION_ACCENTS[role], mode);
}

/**
 * Ink for a projection label, against whichever canvas is live.
 *
 * <p>Derived through {@code markOn}, so this introduces no colour of its own and cannot disagree
 * with the canvas constants.
 */
export function overlayLabelInk(mode = themeMode()) {
  return markOn(canvasFor(mode));
}

/**
 * The same ink at 0.75, for a label's SECOND line.
 *
 * <p>A secondary line has to read as secondary, and on a dark canvas that cannot be done by
 * reaching for a darker grey — `#666` measures 3.30:1 there, which is a hair over the floor for a
 * graphical object and under it for text. Dimming the ink that already works keeps the hierarchy
 * without a second colour decision.
 */
export function overlayLabelInkDim(mode = themeMode()) {
  return rgbaFromHex(overlayLabelInk(mode), 0.75);
}

/**
 * The chip behind a marker's label, as the live canvas at 0.88.
 *
 * <p>⚠ A LABEL'S CONTRAST IS AGAINST ITS CHIP, not against the canvas. The renderer's chip is a
 * hardcoded white, which reads a dark hue well and a LIFTED one badly — `#9FA8DA` is 8.19:1 on the
 * dark canvas and 2.2:1 on white. So any study that brightens itself for dark mode has to bring
 * its chip with it, or it makes its own badge unreadable.
 *
 * <p>Derived from {@code canvasFor} rather than typed, so it introduces no colour and tracks the
 * canvas constants. At 0.88 it masks the candles behind the text while still reading as a chip
 * rather than a panel — the alpha the renderer already used (0.85), rounded up slightly because
 * the padding grew with it.
 */
export function projectionLabelChip(mode = themeMode()) {
  return rgbaFromHex(canvasFor(mode), 0.88);
}

/** Roomier than the renderer's `[1, 4]`, which is what the brief asked for. */
export const PROJECTION_CHIP_PADDING = Object.freeze([3, 6]);

/**
 * The contrast of a role's accent against its own canvas.
 *
 * <p>Exported so the test re-derives the decision rather than restating the numbers in the
 * header — a table of ratios in a comment goes stale the first time a hex moves.
 */
export function accentContrast(role, mode) {
  const hex = accentFor(role, mode);
  if (!hex) return null;
  return contrastRatio(hex, canvasFor(mode));
}
